import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, CheckCircle2, TrendingUp, Lock, Gem, ShieldCheck, Sparkles } from "lucide-react";
import api from "../lib/api";
import SlabPhoto from "../components/SlabPhoto";
import { money } from "../lib/helpers";
import Seo from "../components/Seo";

const HERO_SLAB = "/hero-yamal.webp";
const IMG_VINTAGE = "https://images.unsplash.com/photo-1551306667-f32e7af055f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const IMG_VAULT = "https://images.unsplash.com/photo-1710981855156-1dd4b48e668d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export default function Home() {
  const [tiers, setTiers] = useState([]);
  const [recent, setRecent] = useState([]);
  useEffect(() => { api.get("/tiers").then((r) => setTiers(r.data.tiers)).catch(() => {}); }, []);
  useEffect(() => { api.get("/recent").then((r) => setRecent(r.data.cards || [])).catch(() => {}); }, []);

  return (
    <div className="bg-white">
      <Seo path="/" title="Professional Trading Card Grading Company" description="MAS Grading is a professional trading card grading company — get your cards graded with accurate, consistent grades, tamper-evident slabs, a live population report, and instant online certification verification." jsonLd={[
        { "@context": "https://schema.org", "@type": "Organization", name: "MAS Grading", alternateName: "MAS Card Grading", url: "https://grading.mascertified.com", logo: "https://grading.mascertified.com/mas-logo-black.png", description: "Professional trading card grading, tamper-evident encapsulation, a live population registry, and instant online cert verification.", parentOrganization: { "@type": "Organization", name: "MAS Certified, Inc.", url: "https://mascertified.com" }, sameAs: ["https://mascertified.com"] },
        { "@context": "https://schema.org", "@type": "WebSite", name: "MAS Grading", url: "https://grading.mascertified.com", potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: "https://grading.mascertified.com/lookup?cert={cert}" }, "query-input": "required name=cert" } },
        { "@context": "https://schema.org", "@type": "Service", serviceType: "Trading card grading and certification", provider: { "@type": "Organization", name: "MAS Grading" }, areaServed: "US", url: "https://grading.mascertified.com/submit" },
      ]} />
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <span className="pointer-events-none select-none absolute -right-10 top-4 font-display font-900 text-[22rem] leading-none text-neutral-100 hidden lg:block" style={{ fontWeight: 900 }}>M</span>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div {...fade(0)} className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em]">
              <span className="bg-[#0A0A0A] text-white px-3 py-1 rounded-full">Trusted</span>
              <span className="text-neutral-500">The newest grading authority on the planet</span>
            </motion.div>

            <motion.h1 {...fade(0.06)} className="font-display font-900 text-[#0A0A0A] tracking-tight leading-[0.9] mt-6 text-6xl sm:text-7xl lg:text-[5.6rem]" style={{ fontWeight: 900 }}>
              Trading Card<br />Grading
            </motion.h1>

            <motion.p {...fade(0.12)} className="text-lg text-neutral-500 mt-7 max-w-md leading-relaxed">
              The new standard in professional card grading. Verify, submit, and protect your collection with confidence — from the experts behind MAS Certified, Inc.
            </motion.p>

            <motion.div {...fade(0.18)} className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/lookup" data-testid="hero-verify-cta" className="group inline-flex items-center gap-2 px-8 py-4 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-all duration-300">
                Verify a Cert <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
              <Link to="/submit" data-testid="hero-submit-cta" className="inline-flex items-center gap-2 px-8 py-4 bg-white border border-neutral-300 text-[#0A0A0A] font-bold rounded-full hover:border-[#0A0A0A] transition-colors duration-300">
                Submit an Item
              </Link>
            </motion.div>

            <motion.div {...fade(0.24)} className="mt-8 flex items-center gap-3">
              <div className="flex -space-x-2">
                {["a1", "a2", "a3"].map((a, i) => (<img key={i} src={`/avatars/${a}.jpg`} alt="Collector" className="w-8 h-8 rounded-full border-2 border-white object-cover bg-neutral-200" draggable={false} />))}
              </div>
              <div className="flex text-[#D4AF37]">{Array.from({ length: 5 }).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}</div>
              <span className="text-sm font-medium text-neutral-500">Trusted by collectors worldwide</span>
            </motion.div>
          </div>

          {/* Showcase */}
          <motion.div {...fade(0.15)} className="relative flex justify-center lg:justify-end">
            <div className="relative">
              <div className="floaty relative">
                <SlabPhoto src={HERO_SLAB} className="w-[300px] sm:w-[340px]" />
              </div>
              <div className="absolute -top-5 right-3 bg-[#0A0A0A] text-white rounded-2xl px-4 py-3 shadow-xl z-30">
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">Tamper-Evident</div>
                <div className="font-display font-extrabold text-sm">Graded Slab</div>
              </div>
              <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white rounded-2xl border border-neutral-200 shadow-xl px-5 py-4 z-30 max-w-[230px]">
                <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-bold"><CheckCircle2 size={14} /> Graded by MAS</div>
                <div className="font-display font-extrabold text-[#0A0A0A] mt-1">8000152606</div>
                <div className="text-xs text-neutral-500 leading-tight">2026 Panini Prizm FIFA WC · Lamine Yamal</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* stats band */}
        <div className="bg-[#0A0A0A] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 py-12">
            {[["1–10", "Point Grade Scale"], ["100%", "Verifiable Certs"], ["48h", "Fastest Turnaround"], ["Live", "Population Data"]].map(([k, v], i) => (
              <motion.div key={i} {...fade(i * 0.05)}>
                <div className="font-display font-900 text-4xl md:text-5xl" style={{ fontWeight: 900 }}>{k}</div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-white/45 mt-2 font-semibold">{v}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ RECENTLY GRADED ============ */}
      {recent.length > 0 && (
        <section className="bg-white border-b border-neutral-200 py-20 md:py-28 overflow-hidden" data-testid="recently-graded-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-end justify-between gap-4">
            <motion.div {...fade(0)}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Fresh from the vault</span>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-4 text-[#0A0A0A]">Recently Graded</h2>
              <p className="text-neutral-500 mt-4 max-w-md">A live look at cards recently certified and encapsulated by MAS. Tap any slab to verify it.</p>
            </motion.div>
            <Link to="/lookup" data-testid="recent-verify-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#0A0A0A] hover:gap-3 transition-all duration-300 shrink-0">
              Verify a cert <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-14 relative">
            <div className="flex gap-8 w-max marquee hover:[animation-play-state:paused] pl-6 lg:pl-8">
              {[...recent, ...recent].map((c, i) => (
                <Link
                  key={`${c.certnum}-${i}`}
                  to={`/lookup/${c.certnum}`}
                  data-testid={`recent-slab-${i}`}
                  className="group w-[215px] sm:w-[235px] shrink-0"
                >
                  <div className="rounded-[20px] overflow-hidden bg-neutral-100 ring-1 ring-black/5 shadow-[0_25px_50px_-24px_rgba(0,0,0,0.45)] transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_40px_70px_-24px_rgba(0,0,0,0.5)]">
                    <img src={c.images[0]} alt={c.subject} className="block w-full object-contain" draggable={false} />
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-900 text-xl text-[#0A0A0A] leading-none" style={{ fontWeight: 900 }}>{c.grade}</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4DB8E8]">{c.label}</span>
                    </div>
                    <div className="text-sm font-bold text-[#0A0A0A] truncate mt-1.5">{c.subject}</div>
                    <div className="text-xs text-neutral-500 truncate">{[c.year, c.brand].filter(Boolean).join(" · ")}</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-28 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-28 bg-gradient-to-l from-white to-transparent" />
          </div>
        </section>
      )}

      {/* ============ WHY MAS ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <motion.div {...fade(0)} className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Why grade with MAS</span>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.05] mt-4 text-[#0A0A0A]">A grade is more than a number. It's a promise.</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {[
            { icon: TrendingUp, n: "01", t: "Increase Value", d: "MAS-certified cards command a premium and sell with confidence across every marketplace." },
            { icon: Lock, n: "02", t: "Preserve Forever", d: "Ultrasonically sealed, archival-safe holders protect your card from wear, moisture, and time." },
            { icon: Gem, n: "03", t: "Prove Rarity", d: "Our live population registry shows exactly how scarce your grade truly is." },
          ].map((f, i) => (
            <motion.div key={i} {...fade(0.06 * i)} className="group relative rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-8 hover:bg-white hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)] transition-all duration-400">
              <span className="font-display font-900 text-6xl text-neutral-200 absolute top-5 right-6" style={{ fontWeight: 900 }}>{f.n}</span>
              <f.icon className="text-[#0A0A0A] mb-6" size={30} />
              <h3 className="font-display font-extrabold text-2xl tracking-tight text-[#0A0A0A]">{f.t}</h3>
              <p className="text-neutral-500 mt-3 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ SCALE / VAULT ============ */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-6">
          <motion.div {...fade(0)} className="lg:col-span-7 rounded-3xl overflow-hidden relative min-h-[380px] border border-white/10">
            <img src={IMG_VAULT} alt="Vault" className="absolute inset-0 w-full h-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
            <div className="relative p-8 md:p-12 h-full flex flex-col justify-end">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">The Vault-Grade Standard</span>
              <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-3 max-w-lg">Every card treated like a masterpiece.</h3>
              <p className="text-white/60 mt-4 max-w-md">Controlled-light examination of centering, corners, edges, and surface — by the same experts trusted for autograph authentication.</p>
            </div>
          </motion.div>
          <motion.div {...fade(0.1)} className="lg:col-span-5 rounded-3xl bg-white/5 border border-white/10 p-8 md:p-10 flex flex-col justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">The Scale</span>
              <h3 className="font-display font-extrabold text-3xl tracking-tight mt-3">From Authentic to Gem Mint 10.</h3>
            </div>
            <div className="grid grid-cols-5 gap-2.5 mt-8">
              {Array.from({ length: 10 }).map((_, i) => {
                const gnum = i + 1, gem = gnum === 10, mint = gnum === 9;
                return (<div key={gnum} className={`aspect-square rounded-xl flex items-center justify-center font-display font-900 text-lg ${gem ? "gem-foil text-[#3a2e05]" : mint ? "silver-foil text-[#0A0A0A]" : "bg-white/8 text-white/60 border border-white/10"}`} style={{ fontWeight: 900 }}>{gnum}</div>);
              })}
            </div>
            <p className="text-white/45 text-sm mt-6">Gem Mint 10 = flawless. Fewer than a fraction of submissions ever reach it.</p>
          </motion.div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...fade(0)} className="relative">
          <img src={IMG_VINTAGE} alt="Cards" className="rounded-3xl border border-neutral-200 object-cover w-full h-[440px] shadow-[0_35px_70px_-30px_rgba(0,0,0,0.35)]" />
          <div className="absolute -bottom-6 -right-6 bg-[#0A0A0A] text-white rounded-2xl shadow-xl p-6 hidden sm:block">
            <div className="font-display font-900 text-4xl" style={{ fontWeight: 900 }}>4</div>
            <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">Simple Steps</div>
          </div>
        </motion.div>
        <div>
          <motion.div {...fade(0)}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">How it works</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.05] mt-4 text-[#0A0A0A]">Card to certified slab</h2>
          </motion.div>
          <div className="mt-10">
            {[
              ["Build your submission", "Choose a service tier and log each card's details online."],
              ["Pay securely", "Encrypted Stripe checkout — pay per card at your tier's rate."],
              ["We grade & encapsulate", "Experts assess and seal your card in a tamper-evident holder."],
              ["Track & verify forever", "Follow progress in your portal and verify the cert online anytime."],
            ].map(([t, d], i) => (
              <motion.div key={i} {...fade(0.05 * i)} className="flex gap-5 py-4 border-b border-neutral-100 last:border-0">
                <div className="font-display font-900 text-2xl text-neutral-300 w-10 shrink-0" style={{ fontWeight: 900 }}>0{i + 1}</div>
                <div><h4 className="font-display font-extrabold text-lg text-[#0A0A0A]">{t}</h4><p className="text-neutral-500 mt-1">{d}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="bg-[#FAFAFA] border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <motion.div {...fade(0)} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Service Tiers</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-4 text-[#0A0A0A]">Pick your speed</h2>
            <p className="text-neutral-500 mt-4 text-lg">Per-card pricing. From value grading to same-week Premium turnaround.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
            {tiers.map((t, i) => {
              const featured = t.id === "express";
              return (
                <motion.div key={t.id} {...fade(0.04 * i)} className={`rounded-3xl p-6 flex flex-col ${featured ? "bg-[#0A0A0A] text-white shadow-xl lg:-translate-y-4" : "bg-white border border-neutral-200"}`}>
                  {featured && <span className="self-start text-[10px] font-bold uppercase tracking-widest bg-white text-[#0A0A0A] px-3 py-1 rounded-full mb-3">Most Popular</span>}
                  <h3 className={`font-display font-extrabold text-xl ${featured ? "text-white" : "text-[#0A0A0A]"}`}>{t.name}</h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-display font-900 text-4xl" style={{ fontWeight: 900 }}>{money(t.amount)}</span>
                    <span className={`text-sm mb-1 ${featured ? "text-white/50" : "text-neutral-400"}`}>/ card</span>
                  </div>
                  <p className={`text-sm mt-4 font-semibold ${featured ? "text-white/70" : "text-neutral-600"}`}>{t.turnaround}</p>
                  <p className={`text-xs mt-2 ${featured ? "text-white/40" : "text-neutral-400"}`}>Max value {t.max_value ? money(t.max_value * 100) : "Unlimited"}</p>
                  <Link to="/submit" data-testid={`pricing-${t.id}`} className={`mt-6 text-center py-3 rounded-full font-bold text-sm transition-all duration-300 ${featured ? "bg-white text-[#0A0A0A] hover:bg-neutral-200" : "bg-[#0A0A0A] text-white hover:bg-neutral-800"}`}>Select</Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <motion.div {...fade(0)}>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Trusted by collectors</span>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-4 text-[#0A0A0A] max-w-2xl">Collectors are switching to MAS</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {[
            ["The slabs are gorgeous and the pop report makes reselling effortless. Instant trust.", "Marcus D.", "Card Collector, 14 yrs"],
            ["Fast, precise, and the encapsulation is premium. My grails have never looked better.", "Priya K.", "Anime Memorabilia Dealer"],
            ["We consign hundreds of cards — MAS turnaround and accuracy are unmatched.", "Auction House SF", "Consignment Partner"],
          ].map(([quote, name, role], i) => (
            <motion.div key={i} {...fade(0.06 * i)} className="rounded-3xl border border-neutral-200 bg-white p-8">
              <div className="flex text-[#D4AF37] mb-5">{Array.from({ length: 5 }).map((_, j) => <Star key={j} size={16} fill="currentColor" />)}</div>
              <p className="text-lg font-medium text-[#0A0A0A] leading-relaxed">"{quote}"</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center font-display font-bold">{name[0]}</div>
                <div><div className="font-bold text-sm text-[#0A0A0A]">{name}</div><div className="text-xs text-neutral-500">{role}</div></div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
        <motion.div {...fade(0)} className="relative overflow-hidden rounded-[36px] bg-[#0A0A0A] px-8 py-16 md:p-20">
          <span className="pointer-events-none select-none absolute -right-6 -bottom-16 font-display font-900 text-[16rem] leading-none text-white/[0.04]" style={{ fontWeight: 900 }}>MAS</span>
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-white/60 mb-5"><Sparkles size={13} /> Save 10% with code GRADE10</div>
            <h2 className="font-display font-900 text-4xl md:text-6xl tracking-tight leading-[0.95] text-white" style={{ fontWeight: 900 }}>Ready to certify your collection?</h2>
            <p className="text-white/60 mt-5 text-lg max-w-xl">Create an account, build your submission, and join the new standard in grading.</p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/register" className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0A0A0A] font-bold rounded-full hover:bg-neutral-200 transition-all duration-300">Get Started <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform duration-300" /></Link>
              <Link to="/lookup" className="inline-flex items-center gap-2 px-8 py-4 border border-white/25 text-white font-bold rounded-full hover:border-white transition-colors duration-300"><ShieldCheck size={18} /> Verify a Cert</Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
